import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { ausgangAPI, belegeAPI } from '../lib/api';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Modal zum Erfassen einer manuellen Zahlung auf einen Beleg (Eingang) oder
 * eine Ausgangsrechnung. Erwartet:
 *   quelle: 'eingang' | 'ausgang'
 *   row: { id, betrag_brutto, bezahlt_am, bezahlt_betrag, ... }
 *   onSaved(updated): wird nach erfolgreicher Speicherung aufgerufen
 */
export default function MarkBezahltModal({ open, onClose, slug, quelle, row, onSaved }) {
  const wasPaid = !!(row && row.bezahlt_am);
  const brutto = row ? Number(row.betrag_brutto || 0) : 0;
  const [bezahltAm, setBezahltAm] = useState(todayISO());
  const [betrag, setBetrag] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!row) return;
    setBezahltAm(row.bezahlt_am || todayISO());
    setBetrag(row.bezahlt_betrag != null ? String(row.bezahlt_betrag) : brutto.toFixed(2));
  }, [row, brutto]);

  const save = async (clear = false) => {
    if (!row) return;
    const body = clear
      ? { bezahlt_am: null, bezahlt_betrag: null }
      : { bezahlt_am: bezahltAm, bezahlt_betrag: Number(betrag) };
    setBusy(true);
    try {
      const api = quelle === 'ausgang' ? ausgangAPI : belegeAPI;
      const r = await api.markBezahlt(slug, row.id, body);
      toast.success(clear ? 'Zahlung entfernt' : 'Bezahlt markiert');
      onSaved && onSaved(r.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const offen = brutto - Number(betrag || 0);

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="modal-overlay">
        <Transition.Child
          as={Fragment}
          enter="fade-enter" enterFrom="fade-enter-from" enterTo="fade-enter-to"
          leave="fade-leave" leaveFrom="fade-leave-from" leaveTo="fade-leave-to"
        >
          <div className="modal-backdrop" aria-hidden="true" />
        </Transition.Child>
        <div className="modal-positioner">
          <Transition.Child
            as={Fragment}
            enter="slide-enter" enterFrom="slide-enter-from" enterTo="slide-enter-to"
            leave="slide-leave" leaveFrom="slide-leave-from" leaveTo="slide-leave-to"
          >
            <Dialog.Panel className="modal-panel" style={{ width: 440 }}>
              <div className="modal-header">
                <Dialog.Title className="modal-title">
                  {wasPaid ? 'Zahlung bearbeiten' : 'Als bezahlt markieren'}
                </Dialog.Title>
                <button type="button" className="btn-text" onClick={onClose}><X size={16} /></button>
              </div>

              <div style={{ padding: '0 0 12px' }}>
                <div className="muted small" style={{ marginBottom: 12 }}>
                  Brutto:{' '}
                  <b>
                    {brutto.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                  </b>
                </div>

                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <label>
                    Bezahlt am
                    <input
                      type="date"
                      value={bezahltAm}
                      onChange={(e) => setBezahltAm(e.target.value)}
                    />
                  </label>

                  <label>
                    Bezahlt-Betrag (EUR)
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max={brutto}
                      value={betrag}
                      onChange={(e) => setBetrag(e.target.value)}
                    />
                  </label>
                </div>

                <div className="muted small" style={{ marginTop: 10 }}>
                  Offen nach Buchung:{' '}
                  <b style={{ color: offen > 0.005 ? '#854d0e' : '#166534' }}>
                    {offen.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                  </b>
                  {offen > 0.005 && ' (Teilzahlung)'}
                </div>
              </div>

              <div className="actions" style={{ borderTop: 'none', paddingTop: 12, justifyContent: 'space-between' }}>
                <div>
                  {wasPaid && (
                    <button
                      type="button"
                      className="btn-text"
                      onClick={() => save(true)}
                      disabled={busy}
                      style={{ color: '#991b1b' }}
                    >
                      Zahlung löschen
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => save(false)}
                    disabled={busy || !bezahltAm || !betrag || Number(betrag) <= 0}
                  >
                    {busy ? 'Speichert…' : 'Speichern'}
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
